import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import '../Shared/Menu.css';
import './Login.css';
import logo from '../../images/audithippo.png';
import usePageTitle from '../Shared/usePageTitle';

const RESET_URL = '/rest/api/v1/admin/email/reset-password';

function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

    const [menuOpen, setMenuOpen]         = useState(false);
    const [newPassword, setNewPassword]   = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting]     = useState(false);
    const [error, setError]               = useState('');
    const [done, setDone]                 = useState(false);
    usePageTitle('Reset Password');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!token) {
            setError('Missing or invalid reset token. Please use the link from your email.');
            return;
        }
        if (!newPassword || newPassword.length < 8) {
            setError('Password must be at least 8 characters long.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(RESET_URL, { token, newPassword }, {
                headers: { 'Content-Type': 'application/json' },
            });
            setDone(true);
            setTimeout(() => navigate('/login'), 1800);
        } catch (err) {
            const data = err.response?.data;
            const msg = (data && (data.message || data.error)) || err.message || 'Unable to reset password. The link may have expired.';
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className={`mobile-menu-overlay${menuOpen ? ' show' : ''}`} onClick={() => setMenuOpen(false)} />
            <div className={`mobile-menu${menuOpen ? ' show' : ''}`}>
                <div className="mobile-menu-header">
                    <a href="#" className="logobock d-flex align-items-center gap-2">
                        <div className="hippolog"><img src={logo} alt="AuditHippo" /></div>
                        <span className="brand-name">Audit<span className="hippotxt">Hippo</span></span>
                    </a>
                    <div className="mobile-menu-close" onClick={() => setMenuOpen(false)} role="button" aria-label="Close menu">✕</div>
                </div>
            </div>

            <nav className="navbar-custom">
                <div className="container-custom">
                    <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                            <button className="mobile-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">
                                <span /><span /><span />
                            </button>
                            <div className="d-flex align-items-center gap-2">
                                <a href="#" className="logobock d-flex align-items-center gap-2">
                                    <div className="hippolog"><img src={logo} alt="AuditHippo" /></div>
                                    <span className="brand-name">Audit<span className="hippotxt">Hippo</span></span>
                                </a>
                                <span className="divider-mobile" />
                                <span className="marketing-cloud-text">Marketing Cloud</span>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            <main className="container-main fade-up" style={{ maxWidth: 1200, margin: '0 auto' }}>
                <div className="page-header mb-4">
                    <h1>Reset Password</h1>
                    <p>Choose a new password for your AuditHippo account</p>
                </div>

                <div className="login-screens-row" style={{ display: 'flex', justifyContent: 'center' }}>
                    <div style={{ width: '100%', maxWidth: 520 }}>
                        <p className="login-screen-label">New Password</p>

                        <div className="login-card">
                            <div className="card-body">
                                <div className="d-flex align-items-center gap-2 mb-4">
                                    <div className="logo-box">AH</div>
                                    <span className="brand-name">AuditHippo</span>
                                </div>

                                <span className="login-badge-user">Set New Password</span>

                                <h2 className="login-card-title">{done ? 'Password updated' : 'Set a new password'}</h2>
                                <p className="login-card-subtitle">
                                    {done
                                        ? 'Your password has been updated. Redirecting to sign in…'
                                        : 'Enter a new password below. You will be signed out of any active sessions.'}
                                </p>

                                {!done && (
                                    <form onSubmit={handleSubmit} noValidate>
                                        {!token && (
                                            <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>
                                                Missing reset token. Open the reset link from your email.
                                            </p>
                                        )}

                                        <div className="mb-3">
                                            <label htmlFor="newPassword" className="login-label">New password</label>
                                            <input
                                                type="password"
                                                className="login-input"
                                                id="newPassword"
                                                placeholder="••••••••"
                                                value={newPassword}
                                                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                                                autoComplete="new-password"
                                                disabled={submitting || !token}
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label htmlFor="confirmPassword" className="login-label">Confirm password</label>
                                            <input
                                                type="password"
                                                className="login-input"
                                                id="confirmPassword"
                                                placeholder="••••••••"
                                                value={confirmPassword}
                                                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                                                autoComplete="new-password"
                                                disabled={submitting || !token}
                                            />
                                        </div>

                                        {error && (
                                            <p style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</p>
                                        )}

                                        <button type="submit" className="login-primary-btn" disabled={submitting || !token}>
                                            {submitting ? 'Updating…' : 'Update password'}
                                        </button>
                                    </form>
                                )}

                                <p className="login-footer-text mb-0" style={{ marginTop: 16 }}>
                                    <Link to="/login" className="login-link">Back to sign in</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}

export default ResetPassword;
